// Runtime brand CSS: role tokens only, in @layer nxt.runtime — after nxt.tokens, before nxt.app (layers.css).
import {
  PLATFORM_APPEARANCE_DEFAULT_SCHEME,
  getAppearanceScheme,
  type AppearanceScheme,
} from '../appearance/defaultAppearance';
import { buildRoleTokens } from '../generator/roleTokens';
import { NXT_DARK_SELECTOR, NXT_LIGHT_SELECTOR, validateContrast } from '../generator/emitCss';
import { hexToRgb } from '../generator/oklch';
import { defaultSeeds } from '../seeds/defaultSeeds';
import type { IntentRole, SeedConfig, TokenMap } from '../generator/types';

export type { NamedBrand, AppearanceScheme } from '../appearance/defaultAppearance';

export const RUNTIME_BRAND_STYLE_ID = 'nx-runtime-brand';
export const RUNTIME_LEGACY_NX_STYLE_ID = 'nx-runtime-legacy-nx';

export const RUNTIME_SEED_ROLES = [
  'brand',
  'neutral',
  'info',
  'danger',
  'success',
  'warning',
] as const satisfies readonly IntentRole[];

export type RuntimeSeedRole = (typeof RUNTIME_SEED_ROLES)[number];

/**
 * Every key of a seed record: the intent roles plus the optional `accent`
 * (I-2510) — links and focus rings; absent, they follow the brand. The
 * accent is no intent role (no fills, no on-fill contrast), so it is kept
 * out of `RUNTIME_SEED_ROLES`.
 */
export const RUNTIME_SEED_KEYS = [...RUNTIME_SEED_ROLES, 'accent'] as const;

export type RuntimeSeedKey = (typeof RUNTIME_SEED_KEYS)[number];

/** Seeds a runtime scheme needs. `brand` + `neutral` are required; the rest fall through to the base. */
export type RuntimeBrandSeeds = {
  brand: string;
  neutral: string;
} & Partial<Record<Exclude<RuntimeSeedKey, 'brand' | 'neutral'>, string>>;

const OPTIONAL_SEED_KEYS = ['info', 'danger', 'success', 'warning', 'accent'] as const;

const HEX6 = /^#([0-9a-fA-F]{6})$/;
const HEX3 = /^#([0-9a-fA-F]{3})$/;

export function parseBrandHex(value: string): string | null {
  const v = value.trim();
  const m6 = HEX6.exec(v);
  if (m6) return `#${m6[1].toLowerCase()}`;
  const m3 = HEX3.exec(v);
  if (m3) {
    const h = m3[1];
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return null;
}

export function parseRuntimeBrandSeeds(value: unknown): RuntimeBrandSeeds | null {
  if (!value || typeof value !== 'object') return null;
  const rec = value as Record<string, unknown>;
  const brand = typeof rec.brand === 'string' ? parseBrandHex(rec.brand) : null;
  const neutral = typeof rec.neutral === 'string' ? parseBrandHex(rec.neutral) : null;
  if (!brand || !neutral) return null;
  const out: RuntimeBrandSeeds = { brand, neutral };
  for (const role of OPTIONAL_SEED_KEYS) {
    const raw = rec[role];
    if (raw == null || raw === '') continue;
    const hex = typeof raw === 'string' ? parseBrandHex(raw) : null;
    if (!hex) return null;
    out[role] = hex;
  }
  return out;
}

export function seedsForNamedBrand(name: string): RuntimeBrandSeeds {
  return (
    getAppearanceScheme(name)?.seeds ??
    getAppearanceScheme(PLATFORM_APPEARANCE_DEFAULT_SCHEME)!.seeds
  );
}

function renderBlock(selector: string, tokens: TokenMap): string {
  const lines = Object.entries(tokens)
    .map(([k, v]) => `  --nxt-${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${lines}\n}\n`;
}

function assertContrast(label: string, roles: TokenMap): void {
  const issues = validateContrast(roles);
  if (issues.length) {
    const detail = issues.map((i) => `${i.role} (${i.ratio}:1)`).join(', ');
    throw new Error(`nx-tokens: ${label} on-fill contrast below AA 4.5 for: ${detail}`);
  }
}

function resolvedConfig(seeds: RuntimeBrandSeeds, base: SeedConfig): SeedConfig {
  const brand = parseBrandHex(seeds.brand);
  const neutral = parseBrandHex(seeds.neutral);
  if (!brand || !neutral) {
    throw new Error('nx-tokens: runtime brand seeds must be #rgb or #rrggbb hex');
  }
  const roles = {
    ...base.roles,
    brand,
    neutral,
    info: parseBrandHex(seeds.info ?? brand) ?? brand,
  };
  for (const role of ['danger', 'success', 'warning'] as const) {
    const raw = seeds[role];
    if (!raw) continue;
    const hex = parseBrandHex(raw);
    if (!hex) {
      throw new Error('nx-tokens: runtime brand seeds must be #rgb or #rrggbb hex');
    }
    roles[role] = hex;
  }
  if (!seeds.accent) return { ...base, roles };
  const accent = parseBrandHex(seeds.accent);
  if (!accent) {
    throw new Error('nx-tokens: runtime brand seeds must be #rgb or #rrggbb hex');
  }
  return { ...base, roles, accent };
}

/** Light + dark role maps for a live preview (no CSS, no contrast throw). */
export function buildRuntimeRoleTokens(
  seeds: RuntimeBrandSeeds,
  base: SeedConfig = defaultSeeds
): { light: TokenMap; dark: TokenMap } {
  const config = resolvedConfig(seeds, base);
  return {
    light: buildRoleTokens(config),
    dark: buildRoleTokens({ ...config, mode: 'dark' }),
  };
}

/**
 * Point leftover `--nx-brand-rgb` (and therefore `--nx-primary` / the rgba
 * alpha ladder) at the live `--nxt-brand-bg` fill. Unlayered so it beats
 * the hardcoded navy triplet in `palette.css`.
 */
function emitLegacyNxAliasFromBrandBg(brandBg: string): string {
  const hex = parseBrandHex(brandBg);
  if (!hex) {
    throw new Error('nx-tokens: runtime brand-bg must be #rgb or #rrggbb hex');
  }
  const [r, g, b] = hexToRgb(hex);
  return (
    `/* legacy --nx-brand-rgb so leftover --nx-primary follows the live fill */\n` +
    `:root {\n  --nx-brand-rgb: ${r}, ${g}, ${b};\n}\n`
  );
}

export function emitLegacyNxAliases(
  seeds: RuntimeBrandSeeds,
  base: SeedConfig = defaultSeeds
): string {
  return emitLegacyNxAliasFromBrandBg(buildRuntimeRoleTokens(seeds, base).light['brand-bg']);
}

/**
 * The role tokens of a live look in `@layer nxt.runtime` (layers.css): they
 * beat the generated defaults in `nxt.tokens` and yield to `nxt.app` — the
 * apps' overrides and the kit's design axes (`data-nx-input: flat` sets
 * `--nxt-input-bg` there). Unlayered, as before I-2510, they also beat those
 * axes, and `input: flat` never showed under a design.
 */
export function emitRuntimeRoleCss(light: TokenMap, dark: TokenMap): string {
  assertContrast('runtime light', light);
  assertContrast('runtime dark', dark);
  return (
    `/* runtime brand — @layer nxt.runtime: beats nxt.tokens, yields to nxt.app */\n` +
    `@layer nxt.runtime {\n` +
    renderBlock(NXT_LIGHT_SELECTOR, light) +
    '\n' +
    renderBlock(NXT_DARK_SELECTOR, dark) +
    `}\n`
  );
}

/** Light + dark role maps for a catalog scheme (seed ramp or hand-curated maps). */
export function appearanceSchemeRoleTokens(
  scheme: AppearanceScheme,
  base: SeedConfig = defaultSeeds
): { light: TokenMap; dark: TokenMap } {
  const roles = scheme.tokens
    ? {
        light: { ...buildRoleTokens(base), ...scheme.tokens.light },
        dark: { ...buildRoleTokens({ ...base, mode: 'dark' }), ...scheme.tokens.dark },
      }
    : buildRuntimeRoleTokens(scheme.seeds, base);
  if (!scheme.overrides) return roles;
  return {
    light: { ...roles.light, ...scheme.overrides.light },
    dark: { ...roles.dark, ...scheme.overrides.dark },
  };
}

export function emitAppearanceSchemeCss(
  scheme: AppearanceScheme,
  base: SeedConfig = defaultSeeds
): string {
  const { light, dark } = appearanceSchemeRoleTokens(scheme, base);
  return emitRuntimeRoleCss(light, dark);
}

export function emitAppearanceSchemeLegacyCss(
  scheme: AppearanceScheme,
  base: SeedConfig = defaultSeeds
): string {
  return emitLegacyNxAliasFromBrandBg(appearanceSchemeRoleTokens(scheme, base).light['brand-bg']);
}

/**
 * Role-token CSS for a live brand, in `@layer nxt.runtime`: tenant/runtime
 * overrides beat `@layer nxt.tokens`, the apps' and the design axes'
 * `@layer nxt.app` beats them. Component tokens stay `var()`-bound in the
 * generated sheet and follow.
 */
export function emitRuntimeBrandCss(
  seeds: RuntimeBrandSeeds,
  base: SeedConfig = defaultSeeds
): string {
  const { light, dark } = buildRuntimeRoleTokens(seeds, base);
  return emitRuntimeRoleCss(light, dark);
}
